import React, { memo, ReactElement, useCallback, useEffect, useState } from 'react';
import { ReactComponent as MicIcon } from '@lifesize/ux-assets/vectors/svgIcons/mic.svg';
import { ReactComponent as MicOffIcon } from '@lifesize/ux-assets/vectors/svgIcons/mic-off.svg';
import { useDispatch } from 'react-redux';
import { hooks } from '@lifesize/clients.sdk';
import { setAudioMute } from '@lifesize/clients.sdk/dist/media';
import { audioUnmuteByUser } from 'actions/Call';
import useMicrophoneDeviceId from 'hooks/useMicrophoneDeviceId';
import useMutedByServer from 'hooks/useMutedByServer';
import AudioMonitor from 'utils/AudioMonitor';
import MicLevel from './MicLevel';
import Fab from '@material-ui/core/Fab';
import { makeStyles } from '@material-ui/core/styles';
import { colors } from '@lifesize/ux-assets';
import { WEB_CONFERENCE_DISABLED } from 'constants/Call';
import { injectIntl, defineMessages } from 'react-intl';
import useIsWebConferencing from 'hooks/useIsWebConferencing';
import useIsLocalAudioMuted from 'hooks/useIsLocalAudioMuted';
import { Tooltip } from '@material-ui/core';
import _memoize from 'lodash/memoize';
import { setPillNotificationWithTimeout } from 'actions/Notification';
import { LONG_TIMEOUT } from 'constants/Notification';
import Logger from 'js-logger';

interface AudioButtonProps {
  forceHideTooltip?: boolean; // Needed to allow speaking while muted listener to open a message
  getAudioSample?: Function; // Callback to share the audio monitor instance data
  intl: { formatMessage: Function };
  onUnmute?: Function; // Callback to let outside component know that button has been unmuted
  useLightTheme?: boolean;
  // Lightweight button is a simple mute/unmute state with click handler & NO animation
  useLiteButton?: boolean;
  isPreCall?: boolean;
}

const messages = defineMessages({
  noMicrophoneDetected: {
    id: 'components.avSettingsModal.noMicrophone',
    defaultMessage: 'No microphone detected'
  },
  onSwitchToComputerAudio: {
    id: 'callFooter.PSTN.mute.label',
    defaultMessage: 'Unmute and switch to device audio'
  },
  muteMicrophone: {
    id: 'components.incall.controls.audioMute.tooltip.off',
    defaultMessage: 'Mute microphone'
  },
  unMuteMicrophone: {
    id: 'components.incall.controls.audioMute.tooltip.on',
    defaultMessage: 'Unmute microphone'
  },
  muteAll: {
    id: 'notification.muteAll',
    defaultMessage: 'The moderator has muted your microphone. You cannot unmute.'
  },
  deviceNotAllowed: {
    id: 'components.incall.controls.audioMute.tooltip.unavailable',
    defaultMessage: 'No microphone detected. Please allow access to your device in your browser or system settings.'
  }
});

const AudioButton = (props: AudioButtonProps): ReactElement<any, any> => {
  // Reference to audio monitor to determine if an instance needs to be created or not
  const audioMonitorRef = React.useRef<AudioMonitor>();
  const { intl, useLiteButton, useLightTheme, forceHideTooltip, getAudioSample, onUnmute, isPreCall } = props;
  const [microphoneLevel, setMicrophoneLevel] = useState(0);
  const audioDevice = hooks.useAudioDevice();
  const hasAudioDevices = !!audioDevice;
  const isLocalAudioMuted = useIsLocalAudioMuted();
  const isWebConferencing = useIsWebConferencing();
  const isMutedByServer = useMutedByServer();
  const isModerator = hooks.useIsCurrentUserModerator();
  const isConnected = hooks.useCallConnected();
  const isDisabled =
    !hasAudioDevices || (((!isModerator && isMutedByServer) || (!useLiteButton && !isConnected)) && !isPreCall);
  const isMuted = isLocalAudioMuted || !hasAudioDevices;
  const dispatch = useDispatch();
  const microphoneDeviceId = useMicrophoneDeviceId();
  const [isTooltipOpen, setTooltipOpen] = useState(false);

  const classes = useStyles({
    ...getStyleOverride(isMuted, useLightTheme, useLiteButton)
  });

  const tooltipClasses = useStylesTooltip();

  /** Handler to update mic level state */
  const onAudioMonitorUpdate = useCallback(
    ({ micLevel, sampleData }) => {
      const newMicLevel = Number((micLevel * 100).toFixed(0));
      if (microphoneLevel !== newMicLevel) {
        if (!isMutedByServer) {
          // This prevents updating state when it's not needed to display mic level
          setMicrophoneLevel(newMicLevel);
        }
        if (getAudioSample) {
          // The audio sample data needs to be shared,
          // even if the button is muted
          // (used by AudioButtonWithListener as callback).
          getAudioSample(sampleData);
        }
      }
    },
    [getAudioSample, isMutedByServer, microphoneLevel]
  );

  /**
   * Effect to set muted state
   */
  useEffect(() => {
    if ((isMutedByServer && hasAudioDevices && !isLocalAudioMuted) || isWebConferencing) {
      setAudioMute(true);
    }
  }, [isMutedByServer, isLocalAudioMuted, hasAudioDevices, isWebConferencing]);

  /** To create new audio monitor instance if needed
   * Do not use with lite button */
  useEffect(() => {
    if (!useLiteButton && !audioMonitorRef.current) {
      audioMonitorRef.current = new AudioMonitor(
        (value) => onAudioMonitorUpdate(value),
        () => null
      );
    }
  }, [onAudioMonitorUpdate, useLiteButton, isLocalAudioMuted, isMuted]);

  /** Handler to connect audio monitor om new device
   * Do not use with lite button */
  const connectAudioMonitor = useCallback(
    (audioMonitorRef) => {
      let shouldConnect =
        !useLiteButton &&
        !!microphoneDeviceId &&
        !isMuted &&
        !!audioMonitorRef &&
        audioMonitorRef.current &&
        !audioMonitorRef.current.isConnected;

      if (shouldConnect || !!getAudioSample) {
        audioMonitorRef?.current?.connect(microphoneDeviceId);
      } else if (isMuted) {
        audioMonitorRef?.current?.disconnect();
      }
    },
    [getAudioSample, isMuted, microphoneDeviceId, useLiteButton]
  );

  /** To connect audio monitor
   * Do not use with lite button */
  useEffect(() => {
    if (!useLiteButton && !!audioMonitorRef.current) {
      connectAudioMonitor(audioMonitorRef);
    }
  }, [audioMonitorRef, connectAudioMonitor, useLiteButton]);

  /** To disconnect and destroy audio monitor */
  useEffect(() => {
    return function cleanup() {
      if (audioMonitorRef.current) {
        audioMonitorRef.current
          .destroy()
          .catch((e) => Logger.error(`caught error while destroying audio monitor: ${e}`));
      }
    };
  }, []);

  /** Hide the tooltip with external property. */
  useEffect(() => {
    if (forceHideTooltip) {
      setTooltipOpen(false);
    }
  }, [forceHideTooltip, setTooltipOpen]);

  const onSwitchToComputerAudio = useCallback(() => {
    dispatch({ type: WEB_CONFERENCE_DISABLED });
    setAudioMute(false);
  }, [dispatch]);

  const onAudioMuteClick = useCallback(
    async (isMuted: boolean, isMutedByServer?: boolean) => {
      if (!isMutedByServer) dispatch(audioUnmuteByUser(!isMuted));
      if (!isMuted && !!onUnmute) {
        onUnmute();
      }
      await setAudioMute(isMuted);
    },
    [dispatch, onUnmute]
  );

  /**
   * Solely to display reminder pill that the camera is disabled
   */
  const handleSpanClick = useCallback(() => {
    if (isConnected && (!audioDevice || !audioDevice?.deviceId)) {
      // Make sure tooltip is closed before displaying pill
      setTooltipOpen(false);
      dispatch(setPillNotificationWithTimeout(intl.formatMessage(messages.deviceNotAllowed), LONG_TIMEOUT));
    }
  }, [dispatch, intl, isConnected, audioDevice]);

  const handleOnClick = useCallback(
    (
      isLocalAudioMuted: boolean = false,
      isMutedByServer: boolean = false,
      isModerator: boolean = false,
      hasAudioDevices: boolean = false,
      isWebConference: boolean = false
    ) => {
      if (isWebConference) {
        onSwitchToComputerAudio();
        return;
      }
      if ((isMutedByServer && !isModerator) || !hasAudioDevices) {
        return;
      }
      onAudioMuteClick(!isLocalAudioMuted);
    },
    [onAudioMuteClick, onSwitchToComputerAudio]
  );
  const handleClose = useCallback(() => {
    setTooltipOpen(false);
  }, []);

  const handleOpen = useCallback(() => {
    setTooltipOpen(true);
  }, []);

  const getTooltipMessage = useCallback(
    (
      isLocalAudioMuted: boolean,
      isMutedByServer: boolean,
      hasAudioDevices: boolean,
      isModerator: boolean,
      isWebConferencing?: boolean,
      useLiteButton?: boolean
    ) => {
      if (hasAudioDevices && isLocalAudioMuted && isMutedByServer && !isModerator) {
        return intl.formatMessage(messages.muteAll);
      }
      if (!hasAudioDevices) {
        return intl.formatMessage(messages.noMicrophoneDetected);
      }
      if (!useLiteButton && isWebConferencing) {
        return intl.formatMessage(messages.onSwitchToComputerAudio);
      }
      // Un-muted button
      if (!isLocalAudioMuted) {
        return intl.formatMessage(messages.muteMicrophone);
      }
      // Muted button
      if (isLocalAudioMuted) {
        return intl.formatMessage(messages.unMuteMicrophone);
      }
      return null;
    },
    [intl]
  );

  const getMicrophoneIcon = useCallback(
    (isModerator: boolean, isMutedByServer: boolean, isLocalAudioMuted: boolean, useLiteButton?: boolean) => {
      if (isLocalAudioMuted || isDisabled) {
        return <MicOffIcon data-testid="micOffIcon" />;
      }

      if (useLiteButton) {
        return <MicIcon data-testid="micOnIcon" />;
      }
      if (!isLocalAudioMuted && !isMuted) {
        return <MicLevel micLevel={microphoneLevel} />;
      }
      return <></>;
    },
    [isDisabled, isMuted, microphoneLevel]
  );

  return (
    <Tooltip
      classes={tooltipClasses}
      title={getTooltipMessage(isLocalAudioMuted, isMutedByServer, hasAudioDevices, isModerator, isWebConferencing)}
      placement="top"
      arrow={true}
      onClose={handleClose}
      onOpen={handleOpen}
      open={isTooltipOpen}
    >
      <span data-testid={isLocalAudioMuted ? 'unmute-audio-btn' : 'mute-audio-btn'} onClick={handleSpanClick}>
        <Fab
          classes={classes}
          data-testid="audio-button"
          disabled={isDisabled}
          disableFocusRipple={true}
          size="medium"
          onClick={() =>
            handleOnClick(isLocalAudioMuted, isMutedByServer, isModerator, hasAudioDevices, isWebConferencing)
          }
        >
          {getMicrophoneIcon(isModerator, isMutedByServer, isLocalAudioMuted, useLiteButton)}
        </Fab>
      </span>
    </Tooltip>
  );
};

/** Style selectors that can be dynamically modified */
interface StyleProps {
  borderColor: string;
  color: string;
  disabledBackground: string;
  opacity: number;
}

/** Toggles styles for muted and un-muted states */
const getStyleOverride = _memoize(
  (isMuted: boolean, useLightTheme?: boolean, useLiteButton?: boolean): StyleProps => {
    return {
      borderColor: isMuted ? colors.alertRed : useLightTheme ? colors.squidInk : colors.white,
      color: isMuted ? colors.alertRed : useLightTheme ? colors.squidInk : colors.white,
      opacity: isMuted ? 0.8 : useLightTheme ? 1.0 : 0.6,
      disabledBackground: useLiteButton ? 'transparent !important' : `${colors.squidInk} !important`
    };
  },
  (isMuted, useLightTheme, useLiteButton) => `${isMuted}${useLightTheme}${useLiteButton}`
);

const useStyles = makeStyles(() => ({
  root: {
    backgroundColor: 'transparent',
    border: (btnStyle: StyleProps) => `2px solid ${btnStyle.borderColor}`,
    boxShadow: 'none',
    color: (btnStyle: StyleProps) => btnStyle.color,
    margin: 0,
    opacity: (btnStyle: StyleProps) => btnStyle.opacity,
    '&:hover': {
      backgroundColor: 'transparent',
      borderColor: (btnStyle: StyleProps) => btnStyle.borderColor,
      color: (btnStyle: StyleProps) => btnStyle.color,
      opacity: 1
    },
    '& svg': {
      height: 40,
      width: 'auto'
    }
  },
  disabled: {
    color: `${colors.sentinel} !important`,
    borderColor: `${colors.sentinel} !important`,
    backgroundColor: (btnStyle: StyleProps) => btnStyle.disabledBackground,
    opacity: `1 !important`
  }
}));

const useStylesTooltip = makeStyles(() => ({
  popper: {
    textAlign: 'center',
    zIndex: 999999999 // This is so tooltip is not hidden by z-index of call preview
  }
}));

export default memo(injectIntl(AudioButton));
